import React, { useMemo, useRef, useState } from 'react';
import { COMPANY } from './data/siteData';
import { isValidEmail, isValidName } from './formValidation';
import { leadMessageJunk, MIN_FILL_MS } from './leadSpam';
import { reachFormSubmitGoal } from './yandexMetrika';

const DevopsLeadForm = () => {
    const formLoadedAt = useRef(Date.now());
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [organization, setOrganization] = useState('');
    const [message, setMessage] = useState('');
    const [website, setWebsite] = useState('');
    const [consent, setConsent] = useState(false);
    const [status, setStatus] = useState('idle');
    const [submitting, setSubmitting] = useState(false);

    const emailTrim = email.trim();
    const nameTrim = name.trim();
    const organizationTrim = organization.trim();
    const messageTrim = message.trim();

    const canSubmit = useMemo(
        () =>
            isValidEmail(emailTrim)
            && isValidName(nameTrim)
            && organizationTrim.length >= 2
            && leadMessageJunk(nameTrim, organizationTrim, messageTrim) === ''
            && consent
            && !submitting,
        [emailTrim, nameTrim, organizationTrim, messageTrim, consent, submitting],
    );

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!canSubmit) return;

        setStatus('idle');
        setSubmitting(true);

        const waitMs = MIN_FILL_MS - (Date.now() - formLoadedAt.current);
        if (waitMs > 0) {
            await new Promise((resolve) => {
                setTimeout(resolve, waitMs);
            });
        }

        try {
            const response = await fetch('/api/v1/website/leads', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: emailTrim.toLowerCase(),
                    first_name: nameTrim,
                    organization_name: organizationTrim,
                    message: messageTrim,
                    website,
                    form_loaded_at: formLoadedAt.current,
                    locale: 'ru',
                    product: 'devops',
                    lead_source: 'devops_landing',
                    personal_data_consent: true,
                }),
            });

            if (!response.ok) {
                throw new Error('submit failed');
            }

            reachFormSubmitGoal();
            setStatus('success');
            setEmail('');
            setName('');
            setOrganization('');
            setMessage('');
            setConsent(false);
        } catch {
            setStatus('error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form className="form form--card" onSubmit={handleSubmit} noValidate>
            {status === 'success' ? (
                <div className="form__alert form__alert--success" role="status">
                    <strong>Заявка отправлена</strong>
                    <p>Спасибо! Напишем на почту, с чего начнём разбор контура.</p>
                </div>
            ) : (
                <>
                    {status === 'error' ? (
                        <div className="form__alert form__alert--error" role="alert">
                            <strong>Не удалось отправить заявку</strong>
                            <p>
                                Попробуйте ещё раз или напишите на{' '}
                                <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.
                            </p>
                        </div>
                    ) : null}

                    <input
                        id="devops-website"
                        type="text"
                        name="website"
                        hidden
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                    />

                    <label className="form__label" htmlFor="devops-name">Имя *</label>
                    <input
                        id="devops-name"
                        className="form__input"
                        type="text"
                        name="name"
                        autoComplete="name"
                        placeholder="Как к вам обращаться"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                    />

                    <label className="form__label" htmlFor="devops-organization">Компания *</label>
                    <input
                        id="devops-organization"
                        className="form__input"
                        type="text"
                        name="organization"
                        autoComplete="organization"
                        placeholder="ООО «…»"
                        value={organization}
                        onChange={(e) => setOrganization(e.target.value)}
                        required
                    />

                    <label className="form__label" htmlFor="devops-email">Почта *</label>
                    <input
                        id="devops-email"
                        className="form__input"
                        type="email"
                        name="email"
                        autoComplete="email"
                        placeholder="you@company.ru"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />

                    <label className="form__label" htmlFor="devops-message">Что болит *</label>
                    <textarea
                        id="devops-message"
                        className="form__input form__input--text-area"
                        name="message"
                        placeholder="Кластер, CI, прод, миграция — коротко"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        required
                    />

                    <label className="form__consent">
                        <input
                            type="checkbox"
                            checked={consent}
                            onChange={(e) => setConsent(e.target.checked)}
                        />
                        <span>
                            Согласен(на) на обработку персональных данных ООО «Битапл» по{' '}
                            <a href="/personal-data/" target="_blank" rel="noopener noreferrer">
                                политике обработки персональных данных
                            </a>
                            {' '}и{' '}
                            <a href="/conf-policy/" target="_blank" rel="noopener noreferrer">
                                политике конфиденциальности
                            </a>
                            . Заявку обрабатываем мы, не Яндекс.
                        </span>
                    </label>

                    <button
                        className="button button--primary button--form"
                        type="submit"
                        disabled={!canSubmit}
                    >
                        {submitting ? 'Отправка…' : 'Отправить заявку'}
                    </button>
                </>
            )}
        </form>
    );
};

export default DevopsLeadForm;
